// The credit assessment sheet (TX-US-0027): its sections and rows as the screen names them.
//
// The codes are the API's (trex/counterparties/credit/scales.py); the labels are the template's
// (docs/credit risk/Credit_Assessment_Sheet.docx). What a row's `kind` holds, and the steps
// between the API's sheet and the form's draft, are the shared rows' (../sheets/rows.js).

import { options, sheetOf } from '../sheets/rows.js';

export { DEFAULT_CURRENCY, cleanAmount, displayRow, formatAmount } from '../sheets/rows.js';

const STRENGTH = options(['STRONG', 'Strong'], ['MEDIUM', 'Medium'], ['WEAK', 'Weak']);
const RISK = options(['LOW', 'Low'], ['MEDIUM', 'Medium'], ['HIGH', 'High']);
const COUNTRY_RISK = [...RISK, { code: 'CRITICAL', label: 'Critical' }];

export const SECTIONS = [
  {
    title: 'Counterparty identity',
    rows: [
      { name: 'group_affiliate', label: 'Group / affiliate', kind: 'text' },
      { name: 'registered_address', label: 'Registered address', kind: 'longtext' },
      {
        name: 'relationship',
        label: 'Counterparty category',
        kind: 'grade',
        options: options(['NEW', 'New'], ['EXISTING', 'Existing']),
      },
    ],
  },
  {
    title: 'Commercial and operational profile',
    rows: [
      {
        name: 'years_in_operation',
        label: 'Years in operation',
        kind: 'grade',
        options: options(
          ['UNDER_5', 'Under 5 years'], ['FROM_5_TO_10', '5–10 years'], ['OVER_10', 'Over 10 years'],
        ),
      },
      {
        name: 'estimated_annual_volume',
        label: 'Estimated annual volume (USD)',
        kind: 'grade',
        options: options(['UNDER_1M', 'Under 1M'], ['FROM_1M_TO_3M', '1–3M'], ['OVER_3M', 'Over 3M']),
      },
      { name: 'market_presence', label: 'Market presence / reputation', kind: 'grade', options: STRENGTH },
      {
        name: 'operational_footprint',
        label: 'Operational footprint',
        kind: 'choices',
        options: options(
          ['STORAGE', 'Storage'], ['FLEET', 'Fleet'], ['DEPOT', 'Depot'],
          ['TERMINAL_ACCESS', 'Terminal access'],
        ),
      },
      {
        name: 'trading_history',
        label: 'Trading history with us',
        kind: 'grade',
        options: options(
          ['NONE', 'No history'], ['ONE_TO_THREE_DEALS', '1–3 deals'],
          ['OVER_THREE_DEALS', 'Over 3 deals'],
        ),
      },
      {
        name: 'payment_behaviour',
        label: 'Payment behaviour (general market)',
        kind: 'grade',
        options: options(
          ['ON_TIME', 'On time'], ['OCCASIONAL_DELAYS', 'Occasional delays'],
          ['FREQUENT_DELAYS', 'Frequent delays'],
        ),
      },
      {
        name: 'known_issues',
        label: 'Known issues / past disputes',
        kind: 'grade',
        options: options(['NONE', 'None'], ['MINOR', 'Minor issues'], ['MATERIAL', 'Material issues']),
      },
    ],
  },
  {
    title: 'Payment terms and deal structure',
    rows: [
      {
        name: 'requested_payment_terms',
        label: 'Requested payment terms',
        kind: 'grade',
        options: options(
          ['LC', 'LC'], ['DP', 'DP'], ['CAD', 'CAD'], ['DA', 'DA'], ['PREPAYMENT', 'Prepayment'],
        ),
      },
      {
        name: 'requested_tenor',
        label: 'Requested tenor (DA)',
        kind: 'grade',
        options: options(
          ['UP_TO_15_DAYS', '0–15 days'], ['FROM_15_TO_30_DAYS', '15–30 days'],
          ['OVER_30_DAYS', 'Over 30 days (CEO approval)'],
        ),
      },
      {
        name: 'typical_shipment_size',
        label: 'Typical deal / shipment size',
        kind: 'grade',
        options: options(
          ['UNDER_300_MT', 'Under 300 MT'], ['FROM_300_TO_1000_MT', '300–1,000 MT'],
          ['OVER_1000_MT', 'Over 1,000 MT'],
        ),
      },
      {
        name: 'deal_structure',
        label: 'Deal structure',
        kind: 'grade',
        options: options(
          ['SINGLE_SHIPMENT', 'Single shipment'], ['SPLIT_SHIPMENTS', 'Split shipments'],
        ),
      },
      {
        name: 'currency',
        label: 'Currency',
        kind: 'grade',
        options: options(['USD', 'USD'], ['EUR', 'EUR'], ['LOCAL', 'Local currency']),
      },
      {
        name: 'prepayment_requested',
        label: 'Prepayment requested',
        kind: 'grade',
        options: options(['YES', 'Yes'], ['NO', 'No']),
      },
      {
        name: 'payment_behaviour_indicators',
        label: 'Payment behaviour indicators',
        kind: 'grade',
        options: options(['LOW', 'Low risk'], ['MEDIUM', 'Medium risk'], ['HIGH', 'High risk']),
      },
    ],
  },
  {
    title: 'Country, FX and banking risk',
    rows: [
      { name: 'country_risk', label: 'Country political / security risk', kind: 'grade', options: COUNTRY_RISK },
      { name: 'fx_risk', label: 'FX risk', kind: 'grade', options: COUNTRY_RISK },
      {
        name: 'capital_controls',
        label: 'Capital controls',
        kind: 'grade',
        options: options(['NONE', 'None'], ['MODERATE', 'Moderate'], ['SIGNIFICANT', 'Significant']),
      },
      {
        name: 'banking_system_strength',
        label: 'Banking system strength',
        kind: 'grade',
        options: options(['RELIABLE', 'Reliable'], ['LIMITED', 'Limited'], ['HIGH_RISK', 'High risk']),
      },
      { name: 'regulatory_risk', label: 'Local compliance / regulatory risk', kind: 'grade', options: RISK },
      { name: 'country_notes', label: 'Notes / key observations', kind: 'longtext' },
    ],
  },
  {
    title: 'Counterparty risk analysis',
    rows: [
      { name: 'operational_strength', label: 'Operational strength', kind: 'grade', options: STRENGTH },
      { name: 'financial_strength', label: 'Financial strength / creditworthiness', kind: 'grade', options: STRENGTH },
      // Moved from the KYC review (TX-US-0042).
      {
        name: 'business_reputation',
        label: 'Business reputation',
        kind: 'grade',
        options: options(['POSITIVE', 'Positive'], ['NEUTRAL', 'Neutral'], ['NEGATIVE', 'Negative']),
      },
      {
        name: 'adverse_media',
        label: 'Adverse media',
        kind: 'grade',
        options: options(['NONE', 'None'], ['MINOR', 'Minor'], ['MATERIAL', 'Material']),
      },
      { name: 'sector_risk', label: 'Industry / sector risk', kind: 'grade', options: RISK },
      {
        name: 'behavioural_track_record',
        label: 'Behavioural track record',
        kind: 'grade',
        options: options(['RELIABLE', 'Reliable'], ['MIXED', 'Mixed'], ['WEAK', 'Weak']),
      },
      {
        name: 'risk_factors',
        label: 'Counterparty risk factors',
        kind: 'choices',
        options: options(
          ['OPERATIONAL', 'Operational'], ['FX', 'FX'], ['COUNTRY', 'Country'],
          ['BEHAVIOURAL', 'Behavioural'], ['CONCENTRATION', 'Concentration'],
        ),
      },
      { name: 'execution_risk', label: 'Execution and logistics risk', kind: 'grade', options: RISK },
      { name: 'concentration_risk', label: 'Concentration risk impact', kind: 'grade', options: RISK },
      {
        name: 'overall_credit_risk',
        label: 'Overall credit risk assessment',
        kind: 'grade',
        options: [...RISK, { code: 'VERY_HIGH', label: 'Very high' }],
      },
    ],
  },
  {
    title: 'Limit and rating proposal',
    rows: [
      {
        name: 'internal_rating',
        label: 'Internal rating',
        kind: 'grade',
        options: options(['A', 'A'], ['B', 'B'], ['C', 'C'], ['D', 'D']),
      },
      { name: 'counterparty_limit', label: 'Counterparty limit (CL)', kind: 'limit' },
      { name: 'transaction_limit', label: 'Transaction limit (TL)', kind: 'limit' },
      { name: 'tenor_limit', label: 'Tenor limit', kind: 'tenor' },
      {
        name: 'sop_tier',
        label: 'SOP-04 tier',
        kind: 'grade',
        options: options(
          ['TIER_1', 'Tier 1 (1–3M)'], ['TIER_2', 'Tier 2 (250k–1M)'], ['NEW', 'New'],
          ['PREPAYMENT', 'Prepayment'],
        ),
      },
      {
        name: 'mitigation_tools',
        label: 'Mandatory mitigation tools',
        kind: 'choices',
        options: options(
          ['LC', 'LC'], ['DP', 'DP'], ['SPLIT_SHIPMENTS', 'Split shipments'],
          ['PREPAYMENT', 'Prepayment'], ['ESCROW', 'Escrow'],
        ),
      },
      { name: 'summary_rationale', label: 'Summary rationale', kind: 'longtext' },
    ],
  },
  {
    title: 'Review',
    rows: [
      { name: 'legal_review', label: 'Legal review', kind: 'review' },
      { name: 'commercial_review', label: 'Commercial review', kind: 'review' },
      { name: 'finance_review', label: 'Finance review', kind: 'review' },
    ],
  },
];

const sheet = sheetOf(SECTIONS);

export const ROWS = sheet.rows;
export const { rowLabel, draftFrom, bodyFrom, draftErrors } = sheet;

// What approval is waiting on, as the screen names it.
export const missingLabels = (names = []) => names.map(rowLabel);

// A graded row's answer in the template's words, as other screens quote it - a deal's approver
// reads the overall risk and the internal rating (TX-US-0043). A code the row does not know is
// shown as it is.
export const gradeLabel = (name, code) =>
  ROWS.find((row) => row.name === name)?.options?.find((option) => option.code === code)?.label
    ?? code;
